/**
 * Page Header Context
 *
 * Allows child components/pages to inject page header info (icon, title, description)
 * into the parent AppNavBar, creating a unified header experience.
 *
 * @example
 * ```tsx
 * // In a page component:
 * const { setPageHeader, clearPageHeader } = usePageHeader();
 *
 * useEffect(() => {
 *   setPageHeader({
 *     title: 'Pipeline',
 *     icon: TrendingUpIcon,
 *     description: 'Manage your sales pipeline',
 *   });
 *   return () => clearPageHeader();
 * }, []);
 * ```
 */

import type { SvgIconProps } from '@mui/material';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export interface PageHeaderInfo {
  title: string;
  icon?: React.ComponentType<SvgIconProps>;
  description?: string;
  onBack?: () => void;
  /** Badges/chips to display below the title (e.g., status, priority) */
  badges?: ReactNode;
  /** Page-level action buttons (e.g., Edit, Delete) shown in the navbar right section */
  actions?: ReactNode;
}

interface PageHeaderContextValue {
  pageHeader: PageHeaderInfo | null;
  setPageHeader: (header: PageHeaderInfo) => void;
  clearPageHeader: () => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [pageHeader, setPageHeaderState] = useState<PageHeaderInfo | null>(null);

  const setPageHeader = useCallback((header: PageHeaderInfo) => {
    setPageHeaderState(header);
  }, []);

  const clearPageHeader = useCallback(() => {
    setPageHeaderState(null);
  }, []);

  const value = useMemo(
    () => ({
      pageHeader,
      setPageHeader,
      clearPageHeader,
    }),
    [pageHeader, setPageHeader, clearPageHeader],
  );

  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

export function usePageHeader() {
  const context = useContext(PageHeaderContext);
  if (!context) {
    throw new Error('usePageHeader must be used within a PageHeaderProvider');
  }
  return context;
}
